import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useActivityWaitlist,
  type ActivityWaitlistEntityType,
} from "@/hooks/useActivityWaitlist";
import { useAuth } from "@/hooks/useAuth";
import { Clock, Check, X, LogOut } from "lucide-react";

interface WaitlistButtonProps {
  activityType: ActivityWaitlistEntityType;
  activityId: string;
  isFull: boolean;
  allowWaitingList?: boolean;
  className?: string;
}

function CountdownTimer({ expiresAt }: { expiresAt: string }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft("Utgången");
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${String(secs).padStart(2, "0")}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return (
    <span className="font-mono text-xs font-semibold">{timeLeft}</span>
  );
}

export default function WaitlistButton({
  activityType,
  activityId,
  isFull,
  allowWaitingList = true,
  className,
}: WaitlistButtonProps) {
  const { user } = useAuth();
  const {
    isInQueue,
    myPosition,
    myStatus,
    myEntry,
    waitingCount,
    join,
    leave,
    accept,
    decline,
    isJoining,
    isLeaving,
    isAccepting,
    isDeclining,
  } = useActivityWaitlist(activityType, activityId);

  if (!user) return null;

  // Not full — no waitlist needed
  if (!isFull) return null;

  // Full but waitlist not allowed
  if (!allowWaitingList) {
    return <Badge variant="secondary">Fullbokat</Badge>;
  }

  // User has been offered a spot
  if (myStatus === "offered") {
    return (
      <div className={`flex flex-col gap-2 ${className ?? ""}`}>
        <Badge variant="default" className="gap-1.5 py-1 px-3 text-sm">
          <Clock className="h-3.5 w-3.5" />
          Plats erbjuden!
          {myEntry?.offer_expires_at && (
            <CountdownTimer expiresAt={myEntry.offer_expires_at} />
          )}
        </Badge>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => accept()}
            disabled={isAccepting}
            className="gap-1"
          >
            <Check className="h-3.5 w-3.5" />
            Acceptera
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => decline()}
            disabled={isDeclining}
            className="gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Tacka nej
          </Button>
        </div>
      </div>
    );
  }

  // User is already in queue
  if (isInQueue && myStatus === "waiting") {
    return (
      <div className={`flex items-center gap-2 ${className ?? ""}`}>
        <Badge variant="secondary" className="gap-1 py-1 px-3 text-sm">
          <Clock className="h-3.5 w-3.5" />
          Du är #{myPosition} i kön
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => leave()}
          disabled={isLeaving}
          className="gap-1 text-muted-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
          Lämna kön
        </Button>
      </div>
    );
  }

  // Not in queue — show join button
  return (
    <Button
      variant="outline"
      onClick={() => join()}
      disabled={isJoining}
      className={`gap-1.5 ${className ?? ""}`}
    >
      <Clock className="h-4 w-4" />
      Ställ dig i kö
      {waitingCount > 0 && (
        <span className="text-muted-foreground">({waitingCount} i kön)</span>
      )}
    </Button>
  );
}
